import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { FunctionalTypeFormSelect } from "features/entities/FunctionalTypeFormSelect";
import { LegalTypeFormSelect } from "features/entities/LegalTypeFormSelect";
import {
  getFundListQueryKey,
  useFundCreate,
} from "gen/endpoints/fund-entities/fund-entities";
import { useGlobalEntityCreate } from "gen/endpoints/global-entity/global-entity";
import { useInvestorEntityRelationshipCreate } from "gen/endpoints/investor/investor";
import { useInvestorEntityCreate } from "gen/endpoints/investor-entity/investor-entity";
import { EntityFunctionalTypeEnum } from "gen/model/entityFunctionalTypeEnum";
import { GlobalEntityData } from "gen/model/globalEntityData";
import { GlobalEntityDataRequest } from "gen/model/globalEntityDataRequest";
import { globalEntityCreateBody } from "gen/schemas/global-entity/global-entity";
import { kebabCase } from "lodash-es";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormJurisdictionSelect } from "shared/components/input/select/JurisdictionSelect";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { ConfirmationDialogProps } from "shared/components/modal/confirm/ConfirmationDialog";

export interface AddEntityModalProps {
  fundFamilyId?: string;
  onSuccess?: (args: AddEntityModalArgs, entityData: GlobalEntityData) => void;
}

export interface AddEntityModalArgs {
  investorId?: string; // used only to associate investor entities with investors
}

const AddEntityModal: React.FC<
  ConfirmationDialogProps<
    GlobalEntityData,
    AddEntityModalProps,
    AddEntityModalArgs | undefined
  >
> = ({
  onSuccess,
  onCancel,
  alertProps,
  extraProps: { fundFamilyId, onSuccess: onSuccessExtra },
  args = {},
}) => {
  const { investorId } = args;

  const { t } = useTranslation(["common", "entity", "investorEntity"]);
  const queryClient = useQueryClient();

  const form = useForm<GlobalEntityDataRequest>({
    resolver: zodResolver(globalEntityCreateBody),
    defaultValues: {
      fund_family_id: fundFamilyId,
    },
  });

  const { mutateAsync: createFundEntity } = useFundCreate();
  const { mutateAsync: createInvestorEntity } = useInvestorEntityCreate();
  const { mutateAsync: createGlobalEntity } = useGlobalEntityCreate();

  const createEntity = async (data: GlobalEntityDataRequest) => {
    if (fundFamilyId) {
      return await createFundEntity({
        data: {
          ...data,
          fund_family_id: fundFamilyId,
        },
      });
    }
    if (investorId) {
      return await createInvestorEntity({
        data: {
          ...data,
          investor_id: investorId,
          metadata: {},
        },
      });
    }
    return await createGlobalEntity({
      data,
    });
  };

  const { mutateAsync: createInvestorEntityRelationship } =
    useInvestorEntityRelationshipCreate();

  const legalName = form.watch("legal_name");

  useEffect(() => {
    const fieldState = form.getFieldState("external_id");
    if (!fieldState.isTouched) {
      form.setValue("external_id", kebabCase(legalName));
    }
  }, [form, legalName]);

  return (
    <Modal {...alertProps} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("entity:add.title")}</ModalHeader>
        <Form
          form={form}
          onSubmit={async (entityData) => {
            const entityId = (await createEntity(entityData)).id;
            const isInvestorEntity =
              entityData.functional_type === EntityFunctionalTypeEnum.investor;
            if (isInvestorEntity && investorId && entityId) {
              await createInvestorEntityRelationship({
                investorId,
                data: {
                  investor_entity_ids: [entityId],
                },
              });
            }
            return entityData;
          }}
          onSuccess={async (entityData) => {
            await queryClient.invalidateQueries({
              queryKey: getFundListQueryKey(
                fundFamilyId
                  ? {
                      fund_family_ids: [fundFamilyId],
                    }
                  : undefined,
              ),
            });
            onSuccess(entityData);
            onSuccessExtra?.(args, entityData);
          }}
          successMessage={t("investorEntity:add.success")}
        >
          <ModalBody>
            <FunctionalTypeFormSelect
              isInvestorEntity={!!investorId}
              isFundFamilyRelated={!!fundFamilyId}
            />
            <FormTextInput
              name="legal_name"
              title={t("entity:fullEntityName")}
              type="text"
            />
            {fundFamilyId && (
              <FormTextInput
                name="short_name"
                title={t("entity:shortNameOptional")}
                type="text"
              />
            )}
            <FormTextInput
              name="external_id"
              title={t("entity:externalId")}
              type="text"
            />

            <FormJurisdictionSelect />
            <LegalTypeFormSelect />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={() => onCancel()} />}
              confirm={
                <FormSubmitButton>{t("entity:add.title")}</FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default AddEntityModal;
